import * as z from 'zod';

/** Domain error codes shared by HTTP, MCP and the web client (blueprint §8.4). */
export const ErrorCode = z.enum([
  'UNAUTHENTICATED',
  'NOT_FOUND_OR_DENIED',
  'FORBIDDEN',
  'CONFLICT',
  'ITEM_UNAVAILABLE',
  'INVALID_ARGUMENT',
  'FOLDER_NOT_EMPTY',
  'RATE_LIMITED',
  'BUDGET_EXCEEDED',
  'PROCESSING_UNSUPPORTED',
  'TEMPORARILY_UNAVAILABLE',
]);
export type ErrorCode = z.infer<typeof ErrorCode>;

export const HTTP_STATUS_FOR_ERROR: Record<ErrorCode, number> = {
  UNAUTHENTICATED: 401,
  NOT_FOUND_OR_DENIED: 404,
  FORBIDDEN: 403,
  CONFLICT: 409,
  ITEM_UNAVAILABLE: 409,
  INVALID_ARGUMENT: 400,
  FOLDER_NOT_EMPTY: 409,
  RATE_LIMITED: 429,
  BUDGET_EXCEEDED: 429,
  PROCESSING_UNSUPPORTED: 422,
  TEMPORARILY_UNAVAILABLE: 503,
};

export const ErrorBody = z.object({
  error: z.object({
    code: ErrorCode,
    message: z.string(),
    details: z.record(z.string(), z.unknown()).optional(),
  }),
});
export type ErrorBody = z.infer<typeof ErrorBody>;

/** A refusal or failure with a code every surface shares; its HTTP status is HTTP_STATUS_FOR_ERROR[code]. */
export class DomainError extends Error {
  readonly code: ErrorCode;
  readonly details: Record<string, unknown> | undefined;

  constructor(code: ErrorCode, message: string, details?: Record<string, unknown>) {
    super(message);
    this.name = 'DomainError';
    this.code = code;
    this.details = details;
  }
}

export const notFound = (what = 'Item') => new DomainError('NOT_FOUND_OR_DENIED', `${what} not found or not accessible.`);
export const forbidden = (message = 'Your role does not allow this action.') => new DomainError('FORBIDDEN', message);
export const invalid = (message: string, details?: Record<string, unknown>) => new DomainError('INVALID_ARGUMENT', message, details);
export const unavailable = (message = 'This item is in Trash or no longer available.') => new DomainError('ITEM_UNAVAILABLE', message);

export function isDomainError(error: unknown): error is DomainError {
  return error instanceof DomainError;
}
